/**
 * Platform / viewport detection helpers used across mobile-friendly
 * surfaces of the Care app. Capacitor is imported lazily so this module
 * works in both web and native contexts without throwing when Capacitor
 * isn't bundled (e.g. during unit tests).
 */

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

const MOBILE_BREAKPOINT_PX = 768;
const capacitor = Capacitor || null;

export const MOBILE_BREAKPOINT = MOBILE_BREAKPOINT_PX;

/**
 * True when running inside a Capacitor native shell (iOS / Android).
 */
export function isNativeMobile() {
  try {
    return Boolean(capacitor && capacitor.isNativePlatform && capacitor.isNativePlatform());
  } catch {
    return false;
  }
}

/**
 * True when the current viewport is at-or-below the mobile breakpoint.
 * Safe to call on the server (returns false).
 */
export function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= MOBILE_BREAKPOINT_PX;
}

/**
 * Returns 'ios' | 'android' | 'web' (defaults to 'web' when Capacitor
 * isn't available).
 */
export function getPlatform() {
  try {
    if (capacitor && capacitor.getPlatform) return capacitor.getPlatform();
  } catch {
    /* ignore */
  }
  return 'web';
}

/**
 * Live-updating viewport hook. Fires on resize / orientationchange.
 */
export function useIsMobileViewport(breakpoint = MOBILE_BREAKPOINT_PX) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth <= breakpoint
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (event) => setIsMobile(event.matches);
    setIsMobile(mq.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [breakpoint]);

  return isMobile;
}
